import React, { FC, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Form,
  Grid,
  Input,
  Picker,
  Progress,
  Select,
  Checkbox,
  ModalConfirm,
} from "../../../components";
import { useTranslation } from "react-i18next";
import { AppConfig } from "../../../config";
import { getCodeList } from "country-list";
import { useStore } from "effector-react";
import { ReservationsEndpoint } from "../../../store/endpoints/reservation";
import { onFinish } from "./onFinish";
import { SettingsEndpoint } from "../../../store/endpoints";
import { createEvent, createStore } from "effector";
import { isPresent } from "../../../utils";
import { $selectedLocation } from "../../../store/selected/selectedLocations";

const { Field, useForm } = Form;

const countryList = Object.entries(getCodeList()).sort(
  ([, titleA], [, titleB]) => {
    if (titleA < titleB) return -1;
    if (titleA > titleB) return 1;
    return 0;
  }
);

const tKeyStepCheckout = AppConfig.translationFileNames.step_Checkout;
const tKeyStepFf = AppConfig.translationFileNames.ff;

const $formDataCached = createStore<{ [key: string]: any }>({ country: "be" });
const setFormDataCached = createEvent<{ [key: string]: any }>();
$formDataCached.on(setFormDataCached, (s, v) => v);

const CheckoutForm: FC<{ type: "individual" | "company" }> = ({ type }) => {
  const { t } = useTranslation([tKeyStepCheckout, tKeyStepFf]);
  const { data: settings } = useStore(SettingsEndpoint.store);

  useEffect(() => {
    form.setFieldsValue($formDataCached.getState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [agreed, setAgreed] = useState<boolean>(false);

  const [isVisibleModalConfirm, setIsVisibleModalConfirm] = useState<boolean>(
    true
  );

  const [form] = useForm();

  const { pending, error, data } = useStore(ReservationsEndpoint.reserve.store);

  const isNeedConfirmEmailUsedTooOften = useMemo<boolean>(() => {
    setIsVisibleModalConfirm(true); // reset after close
    return !!isPresent(error?.raw.response.data.errors["contacts.0.email"]);
  }, [error]);

  const location = useStore($selectedLocation);

  return (
    <Form
      form={form}
      onValuesChange={(_, values) => {
        setFormDataCached(values);
      }}
    >
      {pending && <Progress fill />}
      <Grid container>
        <Grid item xs={12} sm={4}>
          <h3>{t("step_Checkout:Contact")}</h3>

          <Field
            name={"is_status_email_used_too_often_confirmed"}
            initialValue={false}
          >
            <></>
          </Field>

          <Field name={"name"} rules={[{ type: "string" }, { required: true }]}>
            <Input label={t("ff:firstName")} />
          </Field>

          <Field
            name={"last_name"}
            rules={[{ type: "string" }, { required: true }]}
          >
            <Input label={t("ff:lastName")} />
          </Field>

          <Field name={"email"} rules={[{ type: "email" }, { required: true }]}>
            <Input label={t("ff:email")} />
          </Field>

          {/*placeholder={"+32 81 00 00 00"} label={}*/}
          <Field name={"phone"} rules={[{ required: true }]}>
            <Input.Phone
              country={"be"}
              enableLongNumbers={true}
              specialLabel={t("ff:phone")}
            />
          </Field>

          <Field name={"birthday"}>
            <Picker.Birthday label={t("ff:birthday")} />
          </Field>
        </Grid>
        <Grid item xs={12} sm={4}>
          <h3>{t("PaymentDetails")}</h3>

          <Field
            name={"street"}
            rules={[{ type: "string" }, { required: true }]}
          >
            <Input label={t("ff:street")} />
          </Field>

          <Field
            name={"country"}
            rules={[{ type: "string" }, { required: true }]}
          >
            <Select variant={"form"} showSearch>
              {countryList.map(([key, label]) => (
                <Select.Option key={key} value={key}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          </Field>

          <Field name={"city"} rules={[{ type: "string" }, { required: true }]}>
            <Input label={t("ff:city")} />
          </Field>

          <Grid container gutter={10}>
            <Grid item sm={8} xs={12}>
              <Field name={"state"} rules={[{ type: "string" }]}>
                <Input label={t("ff:state")} />
              </Field>
            </Grid>
            <Grid item sm={4} xs={12}>
              <Field
                name={"zip"}
                rules={[{ type: "string" }, { required: true }]}
              >
                <Input label={t("ff:zip")} />
              </Field>
            </Grid>
          </Grid>
        </Grid>
        {type === "company" && (
          <Grid item xs={12} sm={4}>
            <h3>{t("CompanyDetails")}</h3>

            <Field name={"company_name"} rules={[{ required: true }]}>
              <Input label={t("ff:companyName")} />
            </Field>

            <Field name={"company_vat"} rules={[{ required: true }]}>
              <Input label={t("ff:companyVat")} />
            </Field>
          </Grid>
        )}
      </Grid>

      {(settings?.terms_and_conditions_url || settings?.privacy_policy_url) && (
        <Checkbox
          style={{ marginBottom: "1.5em" }}
          value={agreed}
          onChange={setAgreed}
        >
          {t("step_Checkout:terms.q")}{" "}
          {settings.terms_and_conditions_url && (
            <a
              href={settings.terms_and_conditions_url}
              style={{ textDecoration: "underline" }}
              target="_blank"
              rel="noreferrer"
            >
              {t("step_Checkout:terms.a")}
            </a>
          )}
          {settings?.terms_and_conditions_url &&
            settings?.privacy_policy_url &&
            ` ${t("and")} `}
          {settings.privacy_policy_url && (
            <a
              href={settings.privacy_policy_url}
              style={{ textDecoration: "underline" }}
              target="_blank"
              rel="noreferrer"
            >
              {t("step_Checkout:policy.a")}
            </a>
          )}
        </Checkbox>
      )}

      {!pending && isNeedConfirmEmailUsedTooOften && (
        <ModalConfirm.Confirm
          visible={isVisibleModalConfirm}
          width={400}
          getContainer={false}
          destroyOnClose={false}
          onConfirm={() => {
            form.setFieldsValue({
              is_status_email_used_too_often_confirmed: true,
            });
            onFinish(form.getFieldsValue());
          }}
          onClose={() => {
            setIsVisibleModalConfirm(false);
          }}
        >
          <>
            <h2>{t("step_Checkout:Attention")}</h2>
            {t("step_Checkout:EmailIsUsedTooOftenQuestion", {
              v: location?.email_used_too_often_hours_limit,
            })}
          </>
        </ModalConfirm.Confirm>
      )}

      <Button
          variant={"primary"}
          disabled={pending || !agreed}
          onClick={(e) => {
            const target = e.currentTarget;
            form
                .validateFields()
                .then(() => {
                  target.disabled = true;
                })
                .then(() => onFinish(form.getFieldsValue()));
          }}
      >
        {t("step_Checkout:Submit")}
      </Button>

      {error && !isNeedConfirmEmailUsedTooOften && (
        <Alert
          style={{ marginTop: 30, display: "inline-block" }}
          variant={"error"}
        >
          {t("step_Checkout:ReservationReserveError")}
        </Alert>
      )}
      {data?.data.checkout_url && (
        <Progress blockWindow>
          {t("step_Checkout:ReservationReserveSuccess")}
        </Progress>
      )}
    </Form>
  );
};

export default CheckoutForm;
